import React, { useEffect, useRef } from "react";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

import useAppContext from "../components/context/useAppContext";
import { useFrigadeAssistantEnabled } from "../components/shared/hooks/useFrigadeAssistantEnabled";
import "./frigade-assistant.css";

const FRIGADE_API_URL = "https://api.frigade.ai";

// Frigade's docs designate userProperties for behavioral context that steers the
// Assistant's answers, and its contents never render in the user's chat transcript.
const SUPPORT_GUIDANCE =
  "When this user asks about an Integration issue: if it can be resolved by actions in the " +
  "Merge dashboard (relinking via Relink with Magic Link, changing Common Model scopes, and " +
  "similar), explain the issue briefly and end by offering to walk them through the fix step " +
  "by step; you can guide them across dashboard pages. When a permission issue only affects a " +
  "Common Model the user may not need, treat disabling that Common Model scope as a valid " +
  "dashboard resolution: say which scope is involved, ask whether they need that data, and " +
  "offer to walk them through turning it off in Scopes > Common Model scopes as an " +
  "alternative to relinking. If the fix requires action by their customer or in the " +
  "third-party system, give a short explanation they can share with their customer and do not " +
  "offer a walkthrough.";
const FRIGADE_CDN_SRC = "https://app.frigade.com/cdn/frigade-assistant.min.js";

type FrigadeAssistantConfig = {
  apiKey: string;
  apiUrl: string;
  assistantId: string;
  userId: string;
  userProperties: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  tools?: Record<string, unknown>;
};

type FrigadeEventHandler = { event: string; handler: (payload: unknown) => void };

type FrigadeAssistantStub = ((config: FrigadeAssistantConfig) => void) & {
  q: unknown[];
  ask?: (opts: { question: string }) => void;
  close?: () => void;
  hide?: () => void;
  getCurrentState?: () => string;
  on: ((event: string, handler: (payload: unknown) => void) => void) & {
    q: FrigadeEventHandler[];
  };
};

declare global {
  interface Window {
    FrigadeAssistant?: FrigadeAssistantStub;
    __mergeFrigadeAssistantListenersRegistered?: boolean;
    // Set when the CDN script fails to load, so ask() entry points (HelpMeSolveButton)
    // can tell a dead SDK from one that is still loading and say the true thing.
    __mergeFrigadeAssistantLoadFailed?: boolean;
  }
}

let pendingPylonOpenTimeout: ReturnType<typeof setTimeout> | undefined;

let frigadeNoticeAnchorChecked = false;

let thumbsDownMissReported = false;

let isHandingOffToPylon = false;

let conversationQuestions: string[] = [];

const MAX_TRANSCRIPT_QUESTIONS = 10;
const MAX_QUESTION_CHARS = 200;
const MAX_STORED_QUESTION_CHARS = 1000;
const QUESTION_CLIP_NOTE = "... (truncated)";

let hasAttemptedPylonHandoff = false;

let hasSeededPylonTicketFields = false;

let seededPylonTicketFields = { subject: "", description: "" };

let bufferedIdentity: string | undefined;

let skipEscalationTurn = false;

const PYLON_ROOT_ID = "pylon-chat";

const COLLAPSED_ASSISTANT_STATES = ["widget", "hidden"];

const PYLON_TICKET_FORM_SLUG = "general-support";

const currentRoute = (): string => (typeof window !== "undefined" ? window.location.pathname : "");

const normalizeQuestion = (text: string): string =>
  text.replace(/\s+/g, " ").trim().slice(0, MAX_STORED_QUESTION_CHARS);

const buildTranscript = (): { text: string; questionCount: number } => {
  const kept = conversationQuestions.slice(-MAX_TRANSCRIPT_QUESTIONS);
  if (kept.length === 0) return { text: "", questionCount: 0 };
  const numbered = kept.map((q, i) => {
    const shown =
      q.length > MAX_QUESTION_CHARS ? `${q.slice(0, MAX_QUESTION_CHARS)}${QUESTION_CLIP_NOTE}` : q;
    return `${i + 1}. ${shown}`;
  });
  const lead = `My most recent question${kept.length === 1 ? "" : "s"} to the dashboard assistant:`;
  return { text: [lead, ...numbered].join("\n"), questionCount: kept.length };
};

const hidePylonChat = (): boolean => {
  const root = document.getElementById(PYLON_ROOT_ID);
  if (root) {
    root.style.display = "none";
    return true;
  }
  return false;
};

const hideFrigadeAssistant = (): void => {
  window.FrigadeAssistant?.hide?.();
};

type PylonTicketFields = {
  subject?: string;
  description?: string;
};

const setPylonTicketFields = ({ subject, description }: PylonTicketFields): void => {
  if (typeof window.Pylon !== "function") return;
  const fields: Record<string, string> = {};
  if (subject?.trim()) fields.subject = subject.trim();
  if (description?.trim()) fields.description = description.trim();
  if (hasSeededPylonTicketFields) {
    const next = {
      subject: fields.subject ?? seededPylonTicketFields.subject,
      description: fields.description ?? seededPylonTicketFields.description,
    };
    window.Pylon("setTicketFormFields", next);
    seededPylonTicketFields = next;
    hasSeededPylonTicketFields = !!(next.subject || next.description);
    return;
  }
  if (Object.keys(fields).length === 0) return;
  window.Pylon("setTicketFormFields", fields);
  seededPylonTicketFields = {
    subject: fields.subject ?? "",
    description: fields.description ?? "",
  };
  hasSeededPylonTicketFields = true;
};

const safePostHogCapture = (event: string, properties: Record<string, unknown>): void => {
  try {
    posthog.capture(event, properties);
  } catch (err) {
    if (process.env.NODE_ENV === "production") return;
    console.error("PostHog capture failed:", err);
  }
};

type PylonHandoffTrigger = "tool" | "negative_rating";

const openPylonChat = (
  fields: PylonTicketFields = {},
  trigger: PylonHandoffTrigger = "tool",
): boolean => {
  const pylonRoot = document.getElementById(PYLON_ROOT_ID);
  const pylonMounted = !!pylonRoot;
  hasAttemptedPylonHandoff = true;
  const transcript = buildTranscript();
  safePostHogCapture("frigade_assistant_pylon_handoff", {
    route: currentRoute(),
    trigger,
    question_count: transcript.questionCount,
    had_transcript: !!transcript.text,
    pylon_mounted: pylonMounted,
  });
  setPylonTicketFields({
    subject: fields.subject,
    description: transcript.text || fields.description,
  });
  if (pylonRoot) pylonRoot.style.display = "";
  if (typeof window.Pylon === "function") {
    window.Pylon("show");
    window.Pylon("showTicketForm", PYLON_TICKET_FORM_SLUG);
  }
  if (!pylonMounted) {
    return false;
  }
  skipEscalationTurn = trigger === "tool";
  conversationQuestions = [];
  seededPylonTicketFields = { subject: "", description: "" };
  const assistant = window.FrigadeAssistant;
  const close = assistant?.close;
  if (typeof close === "function") {
    const state = assistant?.getCurrentState?.();
    if (!state || !COLLAPSED_ASSISTANT_STATES.includes(state)) {
      isHandingOffToPylon = true;
      close();
      setTimeout(() => {
        isHandingOffToPylon = false;
      }, 0);
    }
  }
  hideFrigadeAssistant();
  return true;
};

const PH_NO_CAPTURE_SELECTOR = ".ph-no-capture";

const stampFrigadeIgnore = (node: Node): void => {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (el.closest("[data-fr-ignore]")) return;
  if (el.matches(PH_NO_CAPTURE_SELECTOR)) el.setAttribute("data-fr-ignore", "");
  el.querySelectorAll(PH_NO_CAPTURE_SELECTOR).forEach((match) =>
    match.setAttribute("data-fr-ignore", ""),
  );
};

export const __resetFrigadeModuleStateForTests = (): void => {
  if (process.env.NODE_ENV !== "test") return;
  if (pendingPylonOpenTimeout) clearTimeout(pendingPylonOpenTimeout);
  pendingPylonOpenTimeout = undefined;
  conversationQuestions = [];
  isHandingOffToPylon = false;
  hasAttemptedPylonHandoff = false;
  hasSeededPylonTicketFields = false;
  seededPylonTicketFields = { subject: "", description: "" };
  thumbsDownMissReported = false;
  skipEscalationTurn = false;
  bufferedIdentity = undefined;
  frigadeNoticeAnchorChecked = false;
};

const FrigadeAssistant = (): JSX.Element | null => {
  const { user } = useAppContext();
  const userUuid = user?.uuid;
  const orgId = user?.organization.id;

  const userRef = useRef(user);
  userRef.current = user;

  const initializedRef = useRef(false);

  const { enabled: flagEnabled, hasInitialized: flagInitialized } = useFrigadeAssistantEnabled(
    user?.organization.id,
  );

  useEffect(() => {
    if (!flagInitialized || !flagEnabled || !user) return;
    const apiKey = process.env.REACT_APP_FRIGADE_ASSISTANT_PUBLIC_KEY;
    const assistantId = process.env.REACT_APP_FRIGADE_ASSISTANT_ID;
    if (!apiKey || !assistantId) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    let pylonObserver: MutationObserver | undefined;
    let pylonObserverTimeout: ReturnType<typeof setTimeout> | undefined;

    if (!hasAttemptedPylonHandoff && !hidePylonChat()) {
      pylonObserver = new MutationObserver(() => {
        if (hasAttemptedPylonHandoff) {
          pylonObserver?.disconnect();
          return;
        }
        if (hidePylonChat()) pylonObserver?.disconnect();
      });
      pylonObserver.observe(document.body, { childList: true, subtree: true });
      pylonObserverTimeout = setTimeout(() => pylonObserver?.disconnect(), 30000);
    }

    if (!window.FrigadeAssistant) {
      const queue: unknown[] = [];
      const onQueue: FrigadeEventHandler[] = [];
      const stub = ((config: FrigadeAssistantConfig) => {
        queue.push(config);
      }) as FrigadeAssistantStub;
      stub.q = queue;
      const onFn = ((event: string, handler: (payload: unknown) => void) => {
        onQueue.push({ event, handler });
      }) as FrigadeAssistantStub["on"];
      onFn.q = onQueue;
      stub.on = onFn;
      window.FrigadeAssistant = stub;
    }

    const existing = document.querySelector(`script[src="${FRIGADE_CDN_SRC}"]`);
    if (!existing) {
      const script = document.createElement("script");
      script.src = FRIGADE_CDN_SRC;
      script.async = true;
      script.onerror = (): void => {
        console.warn("Frigade Assistant CDN failed to load");
        Sentry.captureMessage("Frigade Assistant CDN failed to load");
        window.__mergeFrigadeAssistantLoadFailed = true;
      };
      document.body.appendChild(script);
    }

    const [firstName] = (user.name ?? "").trim().split(/\s+/);

    const permissions = [
      {
        permission: "webhook_search",
        description: "Can use the redesigned webhook search experience",
        granted: !!user.is_webhook_search_enabled,
      },
      {
        permission: "two_factor_enabled",
        description: "Has two-factor authentication enabled",
        granted: !!user.is_two_factor_enabled,
      },
      {
        permission: "selective_sync",
        description: "Can configure selective sync filters",
        granted: !!user.is_selective_sync_enabled,
      },
      {
        permission: "destinations",
        description: "Can configure destination warehouses",
        granted: !!user.is_destinations_enabled,
      },
      {
        permission: "onboarding_v2",
        description: "Sees the v2 onboarding flow",
        granted: !!user.is_onboarding_v2_enabled,
      },
      {
        permission: "is_staff",
        description: "Is a Merge employee",
        granted: !!user.is_staff,
      },
    ];

    const tools = {
      handOffToHumanSupport: {
        name: "Open Merge support chat (Pylon)",
        description:
          "Call this to open the live Merge support chat widget (Pylon). Use whenever the user asks for human help, a Merge employee, support, a real person, a ticket, or escalation — or asks to talk to / chat with anyone other than the AI assistant. This is the ONLY way to open Merge's support widget; do not give a URL or tell the user to navigate elsewhere. On success, do not add a follow-up message: the opening Pylon window is the confirmation, and this panel closes. If the tool returns anything other than a confirmation that Pylon is opening, relay that message to the user instead, because nothing will have opened and this panel stays up. " +
          'Write the subject yourself from the conversation so far. The user\'s own questions are attached to the ticket automatically, so the description is only a fallback for when they have not asked anything yet. Write both in the first person, the way the user would write them ("I", "my"), and never refer to yourself or to the AI assistant.',
        inputSchema: {
          type: "object",
          properties: {
            subject: {
              type: "string",
              description:
                'One line naming the actual problem, first person, under 80 characters. Example: "Salesforce contact sync is returning no records".',
            },
            description: {
              type: "string",
              description:
                "Two to five plain-text sentences, first person, no headings or bullets: what I am trying to do, what I have already tried, and what is still broken. Used only when the user has not asked anything yet.",
            },
          },
          required: ["subject"],
          additionalProperties: true,
        },
        execute: async (input?: PylonTicketFields) => {
          const opened = openPylonChat({
            subject: input?.subject,
            description: input?.description,
          });
          if (!opened) {
            return "The support chat isn't loaded yet, so nothing opened. Tell the user it may still appear in a moment, and that they can reload the page or email support@merge.dev if it doesn't.";
          }
          return "Opening Merge support chat (Pylon) now, prefilled with what we discussed.";
        },
      },
      getPlanTier: {
        name: "Get user's plan tier",
        description: "Returns the current organization's billing plan tier.",
        execute: () => {
          const tier =
            userRef.current?.organization.organization_billing_plan?.billing_plan?.plan_tier;
          return tier ?? "Unknown — the plan tier isn't available right now.";
        },
      },
      getProductionLinkedAccountCount: {
        name: "Get production Linked Account count",
        description: "Returns the number of production Linked Accounts in the user's organization.",
        execute: () => {
          const count = userRef.current?.organization.average_production_linked_account_count;
          if (typeof count !== "number") {
            return "The production Linked Account count isn't available right now.";
          }
          return `You have ${count} production Linked Account${count === 1 ? "" : "s"}.`;
        },
      },
      getOrganizationInfo: {
        name: "Get organization info",
        description: "Returns the user's organization name and ID.",
        execute: () => {
          const org = userRef.current?.organization;
          if (!org) return "Organization info isn't available right now.";
          return `Organization: ${org.name} (ID: ${org.id}).`;
        },
      },
      getAccountUuid: {
        name: "Get user's account UUID",
        description: "Returns the signed-in user's stable UUID.",
        execute: () => {
          const uuid = userRef.current?.uuid;
          return uuid ?? "Account UUID isn't available right now.";
        },
      },
    };

    window.FrigadeAssistant({
      apiKey,
      apiUrl: FRIGADE_API_URL,
      assistantId,
      userId: user.uuid,
      userProperties: {
        firstName: firstName || undefined,
        email: user.email,
        role: user.type,
        organization: {
          id: user.organization.id,
          name: user.organization.name,
          plan_tier: user.organization.organization_billing_plan?.billing_plan?.plan_tier ?? null,
        },
        permissions,
        supportGuidance: SUPPORT_GUIDANCE,
      },
      preferences: {
        prefersColorScheme: "light",
        widgetText: "Ask AI",
        iconOnlyWidget: false,
      },
      tools,
    });

    if (!window.__mergeFrigadeAssistantListenersRegistered) {
      window.__mergeFrigadeAssistantListenersRegistered = true;
      window.FrigadeAssistant.on?.("open", () => {
        skipEscalationTurn = false;
        safePostHogCapture("frigade_assistant_opened", { route: currentRoute() });
        if (!frigadeNoticeAnchorChecked) {
          frigadeNoticeAnchorChecked = true;
          setTimeout(() => {
            const anchor = document.querySelector(
              "#frigade-root .fr-chat-panel-input, #frigade-root .fr-chat-panel-clarify-capsule",
            );
            if (!anchor) {
              Sentry.captureMessage("Frigade Assistant sensitive-data notice anchor missing");
            }
          }, 500);
        }
      });
      window.FrigadeAssistant.on?.("ask", (payload: unknown) => {
        const { query } = (typeof payload === "object" && payload !== null ? payload : {}) as {
          query?: unknown;
        };
        const question = typeof query === "string" ? query.trim() : "";
        if (question && skipEscalationTurn) {
          skipEscalationTurn = false;
        } else if (question) {
          const cleaned = normalizeQuestion(question);
          if (cleaned && !conversationQuestions.includes(cleaned)) {
            conversationQuestions.push(cleaned);
            if (conversationQuestions.length > MAX_TRANSCRIPT_QUESTIONS) {
              conversationQuestions.shift();
            }
          }
        }
        safePostHogCapture("frigade_assistant_ask", {
          route: currentRoute(),
          question_length: question.length || undefined,
        });
      });
      window.FrigadeAssistant.on?.("dismiss", () => {
        if (isHandingOffToPylon) {
          isHandingOffToPylon = false;
          return;
        }
        conversationQuestions = [];
        seededPylonTicketFields = { subject: "", description: "" };
        skipEscalationTurn = false;
        safePostHogCapture("frigade_assistant_dismissed", { route: currentRoute() });
      });
      window.FrigadeAssistant.on?.("positive-rating", () =>
        safePostHogCapture("frigade_assistant_positive_rating", { route: currentRoute() }),
      );
      window.FrigadeAssistant.on?.("negative-rating", () => {
        safePostHogCapture("frigade_assistant_negative_rating", { route: currentRoute() });
        if (pendingPylonOpenTimeout) clearTimeout(pendingPylonOpenTimeout);
        pendingPylonOpenTimeout = setTimeout(() => {
          const opened = openPylonChat({}, "negative_rating");
          pendingPylonOpenTimeout = undefined;
          if (!opened && !thumbsDownMissReported) {
            thumbsDownMissReported = true;
            Sentry.captureMessage("Frigade Assistant thumbs-down handoff missed Pylon", "warning");
          }
        }, 500);
      });
    }

    return () => {
      pylonObserver?.disconnect();
      if (pylonObserverTimeout) clearTimeout(pylonObserverTimeout);
      if (pendingPylonOpenTimeout) {
        clearTimeout(pendingPylonOpenTimeout);
        pendingPylonOpenTimeout = undefined;
      }
      const pylonRoot = document.getElementById(PYLON_ROOT_ID);
      if (pylonRoot) pylonRoot.style.display = "";
    };
  }, [flagInitialized, flagEnabled, user]);

  useEffect(() => {
    const identity = `${userUuid ?? ""}:${orgId ?? ""}`;
    if (bufferedIdentity !== undefined && bufferedIdentity !== identity) {
      conversationQuestions = [];
      seededPylonTicketFields = { subject: "", description: "" };
      skipEscalationTurn = false;
      if (hasSeededPylonTicketFields && typeof window.Pylon === "function") {
        window.Pylon("setTicketFormFields", { subject: "", description: "" });
        hasSeededPylonTicketFields = false;
      }
    }
    bufferedIdentity = identity;
  }, [userUuid, orgId]);

  useEffect(() => {
    if (!flagInitialized || !flagEnabled || !userUuid) return undefined;
    if (
      !process.env.REACT_APP_FRIGADE_ASSISTANT_PUBLIC_KEY ||
      !process.env.REACT_APP_FRIGADE_ASSISTANT_ID
    ) {
      return undefined;
    }
    stampFrigadeIgnore(document.body);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach(stampFrigadeIgnore);
        } else if (
          mutation.type === "attributes" &&
          mutation.target.nodeType === Node.ELEMENT_NODE &&
          (mutation.target as Element).matches(PH_NO_CAPTURE_SELECTOR)
        ) {
          (mutation.target as Element).setAttribute("data-fr-ignore", "");
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [flagInitialized, flagEnabled, userUuid, orgId]);

  return null;
};

export default FrigadeAssistant;
